import { reportError } from "utils/diagnostics";
import useFirmwareBundles from "hooks/useFirmwareBundles";
import { firmwareVersionOptions } from "utils/firmware-bundles";
import ActionBarSelect from "components/action-bar-select/action-bar-select";
import { PageTitle } from "components/common/Text";
import { TitleText } from "components/common/text/text.component";
import { ButtonText, PrimaryButton, TextButton } from "components/common/Buttons";
import { PageSection } from "components/common/Wrapper";
import { OptionTypes } from "pages/configs/configs";
import { SoftwareSectionHeader } from "pages/Devices/Detail/components/form-component/DeviceDetail.style";
import React, { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { onEnter } from "utils/general";
import { DateFilterWrapper, TableHeaderCell } from "components/table/table.style";
import { Table, TableHead, TableRow, TableCell, TableBody, TablePagination } from "@mui/material";
import {
  allSoftwareDepot,
  AllSoftwareTypes,
  currentSoftwareDepot,
  dateSearchResultDepot,
  serialSearchResultDepot,
  softwareRecordsDepot,
  softwareRecordTypes,
  versionSearchResultDepot,
} from "../software.depot";
import {
  getAllSoftwareVer,
  getSoftwareRecords,
  getSystemSoftwareVer,
  searchByDate,
  searchBySerial,
  searchByVersion,
  setSofwareVersion,
} from "../software.utilities";
import { FormInput, NxtFieldError } from "components/common/Input";
import style from "../software.module.css";
import { showError } from "utils/notifications";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Spinner } from "components/common/spinner/spinner.component";
import { Popup } from "components/common/popup/popup.component";

const SOFTWARE_SEARCH_CATEGORIES: OptionTypes[] = [
  { label: "Serial Number", value: "serialNumber" },
  { label: "Date Range", value: "dateRange" },
  { label: "Updated Version", value: "version" },
];

export default function SoftwareAutoPage(): JSX.Element {
  const [currentSoftwareVer, setCurrentSoftwareVer] = useState<string>(currentSoftwareDepot.data);
  const [allSoftwareVer, setAllSoftwareVer] = useState<AllSoftwareTypes[]>(allSoftwareDepot.data);
  const [softwareRecords, setSoftwareRecords] = useState<softwareRecordTypes[]>(softwareRecordsDepot.data);
  const [serialSearchResult, setSerialSearchResult] = useState<softwareRecordTypes[]>(serialSearchResultDepot.data);
  const [dateSearchResults, setDateSearchResults] = useState<softwareRecordTypes[]>(dateSearchResultDepot.data);
  const [versionSearchResults, setVersionSearchResults] = useState<softwareRecordTypes[]>(
    versionSearchResultDepot.data,
  );

  const [isUpdatingSoftware, setIsUpdatingSoftware] = useState<boolean>(false);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [startDate, setStartDate] = useState<dayjs.ConfigType>(dayjs(new Date()));
  const [endDate, setEndDate] = useState<dayjs.ConfigType>(dayjs(new Date()));

  const { bundles } = useFirmwareBundles();

  // Set auto update select
  const {
    register,
    reset,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  }: FieldValues = useForm({
    defaultValues: { softwareVersion: currentSoftwareDepot.data },
  });
  // Set search select
  const selectedFilter: string = watch("filterSelection", "");

  // Get selected software version
  const selectedSoftwareVersion: string = watch("softwareVersion");

  // Get queried serial number
  const serialNumber: string = watch("serialNumber");

  // Get queried version number
  const versionNumber: string = watch("version");

  useEffect(() => {
    // Errors are toasted by the axios interceptor; swallow rejections so they
    // don't surface as unhandled promise rejections.
    getSystemSoftwareVer().catch(() => {});
    getSoftwareRecords().catch(() => {});
    getAllSoftwareVer().catch(() => {});
  }, []);

  useEffect(() => {
    const currentSoftware = currentSoftwareDepot.observable.subscribe({
      next: (depotData: string) => {
        reset({ softwareVersion: depotData });
        setCurrentSoftwareVer(depotData);
      },
    });

    const allSoftware = allSoftwareDepot.observable.subscribe({
      next: (depotData: AllSoftwareTypes[]) => {
        setAllSoftwareVer(depotData);
      },
    });

    const allRecords = softwareRecordsDepot.observable.subscribe({
      next: (depotData: softwareRecordTypes[]) => {
        setSoftwareRecords(depotData);
      },
    });

    const serialSearch = serialSearchResultDepot.observable.subscribe({
      next: (depotData: softwareRecordTypes[]) => {
        setSerialSearchResult(depotData);
      },
    });

    const dateSearch = dateSearchResultDepot.observable.subscribe({
      next: (depotData: softwareRecordTypes[]) => {
        setDateSearchResults(depotData);
      },
    });

    const versionSearch = versionSearchResultDepot.observable.subscribe({
      next: (depotData: softwareRecordTypes[]) => {
        setVersionSearchResults(depotData);
      },
    });

    return () => {
      currentSoftware.unsubscribe();
      allSoftware.unsubscribe();
      allRecords.unsubscribe();
      serialSearch.unsubscribe();
      dateSearch.unsubscribe();
      versionSearch.unsubscribe();
    };
  }, []);

  const columnOptions: OptionTypes[] = useMemo(
    () =>
      firmwareVersionOptions(
        (allSoftwareVer ?? []).map(({ swVersion }) => `${swVersion}_MEGAFI2`),
        bundles,
        `${currentSoftwareVer}_MEGAFI2`,
      ).map(({ value, label }) => ({ value: value.replace("_MEGAFI2", ""), label: label.replace("_MEGAFI2", "") })),
    [currentSoftwareVer, allSoftwareVer, bundles],
  );

  const softwareResults = useMemo(() => {
    if (serialSearchResult?.length && selectedFilter === "serialNumber") {
      return serialSearchResult;
    }

    if (dateSearchResults?.length && selectedFilter === "dateRange") {
      return dateSearchResults;
    }

    if (versionSearchResults?.length && selectedFilter === "version") {
      return versionSearchResults;
    }

    return softwareRecords;
  }, [softwareRecords, serialSearchResult, dateSearchResults, versionSearchResults]);

  const saveSelectedSoftware = async (): Promise<void> => {
    const softwareObj: FieldValues = { softwareVersion: selectedSoftwareVersion };
    setIsUpdatingSoftware(true);
    try {
      await setSofwareVersion(softwareObj);
      setCurrentSoftwareVer(selectedSoftwareVersion);
    } catch (error: any) {
      reportError("software.auto.saveSelectedSoftware", error);
      showError(error.message);
    } finally {
      setIsUpdatingSoftware(false);
    }
  };

  // Table
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSearchClick = async (): Promise<void> => {
    setPage(0);
    try {
      if (selectedFilter === "version") {
        let formatedVersion = versionNumber.split("_").shift();

        if (!formatedVersion) {
          return;
        }

        if (!versionNumber?.includes("v")) {
          formatedVersion = `v${formatedVersion}`;
        }

        return await searchByVersion(formatedVersion);
      }

      if (selectedFilter === "dateRange") {
        const formatedStartDate = dayjs(startDate).format("MM/DD/YYYY h:mm:ss");
        const formatedEndDate = dayjs(endDate).format("MM/DD/YYYY h:mm:ss");

        return await searchByDate(formatedStartDate, formatedEndDate);
      }

      return await searchBySerial(serialNumber);
    } catch (error: any) {
      reportError("software.auto.onSearchClick", error);
      showError(error.message);
    }
  };

  const onResetFilter = () => {
    setPage(0);
    reset((formValues: FieldValues) => ({ ...formValues, serialNumber: "" }));
    setSerialSearchResult([]);
    setDateSearchResults([]);
    setVersionSearchResults([]);
  };

  const getFilterOptions = (): JSX.Element => {
    if (selectedFilter === "dateRange") {
      return (
        <DateFilterWrapper>
          <span className={style.dateTimeMargin}>From:</span>
          <DateTimePicker value={startDate} onChange={(newValue) => setStartDate(newValue)} />
          <span className={style.dateTimeMargin}>To:</span>
          <DateTimePicker value={endDate} onChange={(newValue) => setEndDate(newValue)} />
        </DateFilterWrapper>
      );
    }

    if (selectedFilter === "version") {
      return (
        <div className={style.formInputWrapper}>
          <FormInput
            onKeyDown={(event: KeyboardEvent) => onEnter(event, handleSubmit(onSearchClick))}
            {...register("version", {
              required: "Version Number is required",
            })}
            error={errors.version}
            placeholder="Search by version number"
          />
          {errors.version && <NxtFieldError>{errors.version.message}</NxtFieldError>}
        </div>
      );
    }

    return (
      <div className={style.formInputWrapper}>
        <FormInput
          type="number"
          onKeyDown={(event: KeyboardEvent) => onEnter(event, handleSubmit(onSearchClick))}
          {...register("serialNumber", {
            required: "Serial Number is required",
            maxLength: 12,
          })}
          error={errors.serialNumber}
          placeholder="Search by serial number"
        />
        {errors.serialNumber && <NxtFieldError>{errors.serialNumber.message}</NxtFieldError>}
        {errors.serialNumber &&
          (errors.serialNumber.type === "maxLength" || errors.serialNumber.type === "minLength") && (
            <NxtFieldError>Serial Number can not be more than 12 characters in length</NxtFieldError>
          )}
      </div>
    );
  };

  return (
    <>
      <PageTitle>Software</PageTitle>
      <PageSection style={{ marginBottom: "24px" }}>
        <SoftwareSectionHeader>
          <div className={style.titleTextContainer}>
            <TitleText>Auto-update</TitleText>
          </div>
          <div className={style.titleTextContainer}>
            <div style={{ paddingRight: "16px" }}>
              <TitleText>Target Software Version:</TitleText>
            </div>
            <div className={style.selectContainer}>
              <ActionBarSelect
                defaultVal={currentSoftwareVer}
                actionName="softwareVersion"
                options={columnOptions}
                control={control}
                rules={{ required: "Column field is required" }}
              />
            </div>
            <PrimaryButton disabled={currentSoftwareVer === selectedSoftwareVersion} onClick={saveSelectedSoftware}>
              {ButtonText.Save}
            </PrimaryButton>
          </div>
        </SoftwareSectionHeader>
      </PageSection>

      <PageSection>
        <div className={style.searchSelectSelection}>
          <div className={style.selectContainer}>
            <ActionBarSelect
              defaultVal={currentSoftwareVer}
              actionName="filterSelection"
              options={SOFTWARE_SEARCH_CATEGORIES}
              control={control}
              rules={{ required: "Column field is required" }}
            />
          </div>
          {getFilterOptions()}
          <div className={style.paddingLeft}>
            <PrimaryButton onClick={handleSubmit(onSearchClick)}>{ButtonText.Search}</PrimaryButton>
          </div>
          <div className={style.paddingLeft}>
            <TextButton onClick={onResetFilter}>Reset Filter</TextButton>
          </div>
        </div>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableHeaderCell className={style.tableHeaderCell}>Serial Number</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Device Software</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Device Connected Time</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Eligible for Update</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Auto updated</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Auto updated version</TableHeaderCell>
              <TableHeaderCell className={style.tableHeaderCell}>Updated Time</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {softwareResults &&
              softwareResults.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: any, i: number) => (
                <TableRow key={row.uID} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.serialNumber}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.deviceSoftware}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.deviceConnectedTime}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.eligibleForUpdate.toString()}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.autoUpdated.toString()}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.autoupdatedVersion}
                  </TableCell>
                  <TableCell className={style.tableHeaderCell} component="th" scope="row">
                    {row.updatedTime}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <div className={style.paginationContainer}>
          <TablePagination
            component="div"
            count={softwareResults?.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </PageSection>

      <Popup title="Updating software..." open={isUpdatingSoftware}>
        <Spinner />
      </Popup>
    </>
  );
}
